import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import PropTypes from 'prop-types';
import { get, pick, omit, times } from 'lodash';
import { formValueSelector } from 'redux-form';

import buildEventDispatcher from '../../lib/event-dispatcher';
import CommitForm, { FORM_NAME } from '../../components/commit-form';
import MemberFieldsForm, {
  ALLOWED_MEMBER_FIELDS_PROPS,
} from '../../components/commit-form/member-fields-form';
import { tokenizeCard } from '../../lib/card-functions';
import { Money } from '../../lib/money';
import Membership from '../../lib/membership';
import { buildDesktopConsumerFieldValues, buildSpecialConsumerFieldInitialValues } from '../../lib/functions';
import { reportError } from '../../lib/error-reporter';
import buildMemberFieldValues from '../../lib/desktop-member-fields-initial-values-builder';
import {
  submitDashboardCommitForm as submitCommitForm,
  showModalInnerAlert,
  projectPurchase,
  hideCommitModal,
  showAlert,

  COMMIT_NAMESPACE,
  ALERT_PURCHASE_SAVED
} from '../../actions';
import { formatContentWithEmoji } from '../../components/alert';
import {
  getCurrentUserMembership,
  getCurrentPurchase,
  getMemberConsumerFieldSchemas,
} from '../../selectors/purchase-selector';

import withDashboardProjectionFetcher from '../../../components/hoc/with-dashboard-projection-fetcher';

const COMPONENT_ID = 'DesktopDashboardCommitContainer';

class Commit extends Component {
  static propTypes = {
    fetchDashboardProjection: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = { redirectTo: null };
    this.onSubmit = this.onSubmit.bind(this);
    this.finishSubmit = this.finishSubmit.bind(this);
    this.showSubmitionErrorWarnings = this.showSubmitionErrorWarnings.bind(this);
    this.onMemberFieldsContinue = this.onMemberFieldsContinue.bind(this);
    this.onEditMemberFields = this.onEditMemberFields.bind(this);
    this.projectPurchase = this.projectPurchase.bind(this);
    this.enableSubmit = this.enableSubmit.bind(this);
    this.disableSubmit = this.disableSubmit.bind(this);

    this.state = {
      submitDisabled: false,
      showMemberFields: props.memberConsumerFieldSchemas.length > 0 ||
        props.billingAddressConsumerFieldSchema,
    };
  }

  componentWillMount() {
    this.projectPurchase();
  }

  componentDidMount() {
    this.props.eventDispatcher.dispatchDashboardCommitLoadedEvent();
  }

  onSubmit(props) {
    this.disableSubmit();
    const {
      user = {},
      purchase,
      currentMembership,
      authentication,
      memberConsumerFieldSchemas,
      billingAddressConsumerFieldSchema,
    } = this.props;
    const email = user.email || props.email || props.auth.email;
    const allowedAmounts = {
      allowedShareAmount: currentMembership.maxShareAmount,
      allowedFees: currentMembership.maxFees,
    };
    const consumerFieldValues =
      buildDesktopConsumerFieldValues(props, { ...memberConsumerFieldSchemas, billingAddressConsumerFieldSchema });
    const data = { consumerFieldValues, ...omit(props, 'memberFields') };

    this.formProps = {
      ...data,
      ...allowedAmounts,
      user
    };

    if (props.card.paymentSourceId && props.card.paymentSourceId !== 'new') {
      return this.props.actions.submitCommitForm(purchase, this.formProps, authentication.token)
        .then(() => this.onSubmitSuccess())
        .catch((error) => {
          const content = formatContentWithEmoji(error.message, '‼️');
          this.props.actions.showModalInnerAlert(content);
          this.enableSubmit();
          const params = omit(this.formProps, 'auth');
          reportError({
            error,
            component: COMPONENT_ID,
            params,
          });
        });
    }

    // When tokenize card finishes, the cardTokenized callback on CommitForm is called
    // See also card-fields.js
    tokenizeCard(props.card, email, purchase.paymentPlatform);
  }

  onMemberFieldsContinue() {
    this.setState({ showMemberFields: false });
  }

  onEditMemberFields() {
    this.setState({ showMemberFields: true });
  }

  finishSubmit(cardToken) {
    const { purchase, authentication: { token } } = this.props;

    const data = {
      ...this.formProps,
      card: {
        ...this.formProps.card,
        platformId: purchase.paymentPlatform.id,
        paymentSource: cardToken,
      },
    };
    this.props.actions.submitCommitForm(purchase, data, token)
      .then(() => {
        return this.onSubmitSuccess();
      })
      .catch((error) => {
        let content;

        if (error.is401) {
          content = 'Invalid password';
        } else {
          content = formatContentWithEmoji(error.message, '‼️');
        }
        reportError({
          error,
          component: COMPONENT_ID,
          params: {
            purchaseId: purchase.id,
            ...omit(data, 'auth'),
            token,
          },
        });
        this.props.actions.showModalInnerAlert(content);
        this.formProps = {};
        this.enableSubmit();
      });
  }

  projectPurchase() {
    const purchase = pick(this.props.purchase, ['id', 'group']);
    purchase.memberships = [
      {
        id: this.props.currentMembershipId,
        claimedSlots: this.props.toProject.claimedSlots,
        allowedShareAmount: this.props.toProject.shareAmount,
      },
    ];
    this.props.actions.projectPurchase(purchase, COMMIT_NAMESPACE);
  }

  onSubmitSuccess = () => {
    const { actions, fetchDashboardProjection, purchase } = this.props;
    const {hideCommitModal, showAlert} = actions;

    hideCommitModal();
    fetchDashboardProjection()
      .catch(() => window.location = `/purchases/${purchase.id}`);
    showAlert(ALERT_PURCHASE_SAVED);
  }

  showSubmitionErrorWarnings(errorMessage) {
    let text;
    if (errorMessage) {
      text = <span>{errorMessage}.</span>;
    } else {
      text = <span>Something went wrong.</span>;
    }
    const content = formatContentWithEmoji(text, '‼️');
    this.props.actions.showModalInnerAlert(content);
    this.enableSubmit();
  }

  disableSubmit() {
    this.setState({
      submitDisabled: true,
    });
  }

  enableSubmit() {
    this.setState({
      submitDisabled: false,
    });
  }

  render() {
    const { purchase, purchase: { merchant } } = this.props
    if (this.state.showMemberFields) {
      return (
        <MemberFieldsForm
          {...pick(this.props, ALLOWED_MEMBER_FIELDS_PROPS)}
          showShareInfoInMemberFieldsForm={this.props.showShareInfoInMemberFieldsForm}
          onSubmit={this.onMemberFieldsContinue}
        />
      );
    }

    return (
      <CommitForm
        currentMembership={this.props.currentMembership}
        membershipOptedIn={this.props.membershipOptedIn}
        showOptInMarketing={merchant.requestOptInMarketing}
        user={this.props.user}
        editing={this.props.editing}
        purchase={purchase}
        initialValues={this.props.initialValues}
        onSubmit={this.onSubmit}
        cardTokenized={this.finishSubmit}
        cardError={this.showSubmitionErrorWarnings}
        onClose={() => { }}
        onLogin={this.projectPurchase}
        onLogout={this.projectPurchase}
        disableSubmit={this.disableSubmit}
        enableSubmit={this.enableSubmit}
        submitDisabled={this.state.submitDisabled}
        onEditMemberFields={this.onEditMemberFields}
        hasMemberFields={this.props.memberConsumerFieldSchemas.length > 0}
        claimedSlots={this.props.capturedClaimedSlots}
        otherMembersSlots={this.props.otherMembersSlots}
        detectedEmail={this.props.detectedEmail}
        allowSignin
      />
    );
  }
}

const mapStateToProps = (state) => {
  const { users, session, memberships, projections } = state;
  const user = users.byId[users.current];
  const purchase = getCurrentPurchase(state);

  // FIXME: fallback condition should not be here, projections should be already called at this point,
  //  this may be related to `setCurrentMembership` action and how we handle the responses of it
  const defaultMembership = {
    id: memberships.current,
    shareAmount: Money.zero(purchase.currencyCode),
  };
  const projectedMembership = get(projections,
    `${COMMIT_NAMESPACE}.memberships.${memberships.current}`, defaultMembership);
  let memberConsumerFieldSchemas = getMemberConsumerFieldSchemas(state);
  const currentMembership = getCurrentUserMembership(state);

  const billingAddressConsumerFieldSchema = get(getMemberConsumerFieldSchemas(state)
    .filter((field) => field.name === 'billing_address'), '[0]', null);

  const selector = formValueSelector(FORM_NAME);

  const claimedSlotsTouched = get(state, 'form.commitForm.fields.claimedSlots.touched', false);

  const claimedSlots = claimedSlotsTouched ?
    selector(state, 'claimedSlots') : get(projectedMembership, 'claimedSlots', 1);
  const defaultAllowedShare = Membership.defaultContributionAmount({
    membership: currentMembership,
    minContribution: purchase.group.minContribution,
    currencyCode: purchase.currencyCode,
  });
  const shareAmount = selector(state, 'allowedShareAmount') || defaultAllowedShare;

  const memberFieldValues = buildMemberFieldValues({
    schemas: memberConsumerFieldSchemas,
    persistedValues: get(getCurrentUserMembership(state), 'consumerFieldValues', []),
    claimedSlots,
  }).filter((field) => field.name !== 'billing_address');

  let billingAddressMemberFieldValue = null;
  let billingInitialValue = {};

  if (billingAddressConsumerFieldSchema && billingAddressConsumerFieldSchema.options.length) {
    billingAddressMemberFieldValue = JSON.stringify(get(billingAddressConsumerFieldSchema, 'options[0].value', null));
    billingInitialValue = {
      [billingAddressConsumerFieldSchema.name]: billingAddressMemberFieldValue,
    }
  }

  const showShareInfoInMemberFieldsForm = memberConsumerFieldSchemas.length > 0;
  memberConsumerFieldSchemas = getMemberConsumerFieldSchemas(state)
    .filter((field) => field.name !== 'billing_address');

  const specialConsumerFieldValues = buildSpecialConsumerFieldInitialValues(memberConsumerFieldSchemas);

  if (memberFieldValues.length) {
    memberFieldValues[0] = { ...memberFieldValues[0], ...specialConsumerFieldValues };
  } else {
    memberFieldValues.push(specialConsumerFieldValues);
  }

  if (memberFieldValues.length && memberFieldValues.length < claimedSlots) {
    times(claimedSlots - memberFieldValues.length, () => ({})).forEach((slot) => {
      memberFieldValues.push(slot);
    });
  }

  const otherMembersSlots = purchase.committedSlots - get(currentMembership, 'claimedSlots', 0);

  return {
    eventDispatcher: buildEventDispatcher(state),
    authentication: session.authentication,
    user,
    editing: false,
    purchase,
    currentMembership: projectedMembership,
    membershipOptedIn: get(currentMembership, 'optInMarketing', false),
    organizerSignup: false,
    capturedClaimedSlots: selector(state, 'claimedSlots'),
    memberConsumerFieldSchemas,
    showShareInfoInMemberFieldsForm,
    billingAddressConsumerFieldSchema,
    otherMembersSlots,
    detectedEmail: users.existence,
    toProject: {
      claimedSlots,
      shareAmount,
    },
    initialValues: {
      claimedSlots,
      allowedShareAmount: defaultAllowedShare,
      memberFields: {
        slots: memberFieldValues.length > 0 ? memberFieldValues : [{}],
      },
      ...billingInitialValue,
      optInMarketing: false,
      card: {
        paymentSourceId: 'new',
      },
      auth: {
        email: '',
      },
    },
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators({
      submitCommitForm,
      showModalInnerAlert,
      projectPurchase,
      hideCommitModal,
      showAlert
    }, dispatch),
  };
};

export default compose(
  withDashboardProjectionFetcher,
  connect(mapStateToProps, mapDispatchToProps)
)(Commit);




// WEBPACK FOOTER //
// ./src/desktop/containers/purchase-dashboard/commit.container.js